import { useCallback, useEffect, useMemo, useState } from "react";
import { toHttpUrl } from "../utils/audioUrl";
import { audioManager } from "../utils/audioManager";
import generateTextToAudio from "../utils/generateTextToAudio";

export interface UseAudioControlsArgs {
  id?: string;
  outputAudioUrl?: string;
  userId?: string;
  onResolvedAudioUrl?: (id: string, url: string) => void;
}

export interface UseAudioControls {
  isLoadingAudio: boolean;
  isPlaying: boolean;
  togglePlay: () => Promise<void>;
}

export function useAudioControls(args: UseAudioControlsArgs): UseAudioControls {
  const { id, outputAudioUrl, userId, onResolvedAudioUrl } = args;

  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [localAudioUrl, setLocalAudioUrl] = useState<string | undefined>(
    undefined,
  );
  const [isPlaying, setIsPlaying] = useState(false);

  const normalizedExisting = useMemo(() => {
    const raw = localAudioUrl ?? outputAudioUrl;
    return raw ? toHttpUrl(raw) : undefined;
  }, [localAudioUrl, outputAudioUrl]);

  useEffect(() => {
    const unsubscribe = audioManager.subscribe(
      ({ currentUrl, isPlaying: playing }) => {
        setIsPlaying(
          Boolean(
            normalizedExisting && currentUrl === normalizedExisting && playing,
          ),
        );
      },
    );
    return unsubscribe;
  }, [normalizedExisting]);

  const togglePlay = useCallback(async () => {
    if (!id) return;
    const existing = normalizedExisting;
    if (existing) {
      if (isPlaying) {
        audioManager.pause();
        return;
      }
      await audioManager.playUrl(existing);
      return;
    }
    try {
      setIsLoadingAudio(true);
      const { data } = await generateTextToAudio({ messageId: id, userId });
      const urlRaw = data?.output_audio_url;
      const url = urlRaw ? toHttpUrl(urlRaw) : undefined;
      if (url) {
        setLocalAudioUrl(url);
        if (onResolvedAudioUrl) onResolvedAudioUrl(id, url);
        await audioManager.playUrl(url);
      }
    } finally {
      setIsLoadingAudio(false);
    }
  }, [id, isPlaying, normalizedExisting, onResolvedAudioUrl, userId]);

  return { isLoadingAudio, isPlaying, togglePlay };
}

export default useAudioControls;
